import { CarouselItemId } from '@homex/se-widget-flow-types';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  setCarouselNavPointerX,
  setCurrentCarouselItem,
  setServiceType,
  setServiceTypeRadioValue,
} from 'actions';
import { Phrase } from 'components/Phrase';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { CarouselReduxReducer, IState, ServiceType } from 'typings';

import {
  ButtonDetails,
  ButtonGroup,
  ButtonHeader,
  ButtonText,
  Chevron,
  Heading,
  Label,
  MaintenanceSvg,
  QuoteSmallSvg,
  QuoteSvg,
  Radio,
  RepairSvg,
} from './styles';

const POINTER_IMAGE_WIDTH = 69;

export const CarouselItemSelectServiceTypeTop = () => {
  const dispatch = useThunkDispatch();
  const { serviceTypeRadioValue, validServiceTypes, viewportSize } =
    useSelector((state: IState) => ({
      serviceTypeRadioValue: state.userInput.serviceTypeRadioValue,
      validServiceTypes: state.client.validServiceTypes,
      viewportSize: state.device.viewportSize,
    }));

  const handleNavPointerPositionChange = (pointerX: number) => {
    dispatch(setCarouselNavPointerX(pointerX));
  };

  const handleServiceTypeChange = (serviceType: ServiceType) => {
    dispatch(setServiceType(serviceType));
  };

  const handleServiceTypeRadioValueChange = (serviceType: ServiceType) => {
    dispatch(setServiceTypeRadioValue(serviceType));
  };

  const navigateToNextItem = (serviceType: ServiceType) => {
    dispatch(setServiceTypeRadioValue(serviceType));

    switch (serviceType) {
      case ServiceType.SALES:
        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            CarouselItemId.ContactInfo,
          ),
        );

        break;
      case ServiceType.REPAIR:
        dispatch(
          setCurrentCarouselItem(
            CarouselReduxReducer.SERVICE,
            CarouselItemId.IssueSpecifier,
          ),
        );

        break;
      case ServiceType.MAINTENANCE:
        // TODO
        break;
      default:
        // Do nothing
        break;
    }
  };

  const handleSelectionChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { parentElement, value } = event.currentTarget;
    const serviceType = value as ServiceType;

    if (
      (event.type === 'click' && viewportSize === 'large') ||
      (event.type === 'change' && viewportSize !== 'large')
    ) {
      return;
    }

    if (viewportSize !== 'large') {
      handleServiceTypeChange(serviceType);
      navigateToNextItem(serviceType);
    } else if (parentElement) {
      const { offsetLeft } = parentElement;
      const { width } = parentElement.getBoundingClientRect();
      const pointerX = offsetLeft + width / 2 - POINTER_IMAGE_WIDTH / 2;

      handleServiceTypeRadioValueChange(serviceType);
      handleNavPointerPositionChange(pointerX);
    }
  };

  const getSortedServiceTypes = () => {
    switch (validServiceTypes.length) {
      case 3:
        return [ServiceType.SALES, ServiceType.REPAIR, ServiceType.MAINTENANCE];
      case 2:
        if (validServiceTypes.includes(ServiceType.REPAIR)) {
          if (validServiceTypes[0] === ServiceType.REPAIR) {
            return validServiceTypes;
          } else {
            return validServiceTypes.reverse();
          }
        }

        return [ServiceType.SALES, ServiceType.MAINTENANCE];
      default:
        return validServiceTypes;
    }
  };

  const getServiceTypeSvg = (serviceType: string) => {
    switch (serviceType) {
      case ServiceType.SALES:
        return (
          <>
            <QuoteSvg />
            <QuoteSmallSvg />
          </>
        );
      case ServiceType.REPAIR:
        return <RepairSvg />;
      case ServiceType.MAINTENANCE:
        return <MaintenanceSvg />;
      default:
        // Do nothing
        return null;
    }
  };

  const sortedValidServiceTypes: Array<ServiceType> = getSortedServiceTypes();
  const validServiceTypeLength = sortedValidServiceTypes.length;

  return (
    <>
      <Heading>
        <Phrase id="SERVICE_TYPE.HEADER" />
      </Heading>
      <ButtonGroup serviceTypeLength={validServiceTypeLength}>
        {sortedValidServiceTypes.map((serviceType) => (
          <Label key={serviceType} serviceTypeLength={validServiceTypeLength}>
            <Radio
              checked={serviceTypeRadioValue === ServiceType[serviceType]}
              name="Button-radio"
              onChange={handleSelectionChange}
              onClick={handleSelectionChange}
              type="radio"
              value={ServiceType[serviceType]}
            />
            {getServiceTypeSvg(serviceType)}
            <ButtonText>
              <ButtonHeader>
                <Phrase id={`SERVICE_TYPE.${serviceType}.HEADER`} />
              </ButtonHeader>
              <ButtonDetails>
                <Phrase id={`SERVICE_TYPE.${serviceType}.BODY`} />
              </ButtonDetails>
            </ButtonText>
            <Chevron />
          </Label>
        ))}
      </ButtonGroup>
    </>
  );
};
